/* eslint-disable @typescript-eslint/ban-ts-comment,no-restricted-syntax,@typescript-eslint/no-explicit-any */
import { useState, useMemo, useCallback } from 'react';
import {
  Connection as RPCConnection,
  Connection,
  PublicKey,
  SystemProgram, SYSVAR_RENT_PUBKEY,
  Transaction,
  TransactionInstruction,
} from '@solana/web3.js';
import axios from 'axios';
import BN from 'bn.js';
import bs58 from 'bs58';
import { sha256 } from 'js-sha256';
import {
  AccountLayout,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  Token,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import * as anchor from '@project-serum/anchor';
import {
  AnchorProvider,
  Program as AnchorProgram,
} from '@project-serum/anchor';
import { AnchorWallet } from '@solana/wallet-adapter-react';
import ConsoleHelper from '../utils/consoleHelper';
import { GUMDROP_DISTRIBUTOR_ID, SOLANA_HOST } from '../utils/consts';
import { useSolanaWallet } from '../contexts/SolanaWalletContext';
import {
  CGC_TOKEN_CLAIM_PROGRAM_ID,
  SOLCHICK_TOKEN_ADDRESS_ON_SOL,
  TOKEN_VAULT_WALLET,
  URL_SUBMIT_CLAIM,
} from '../utils/solchickConsts';
import { sleep } from '../utils/helper';
import { getAssociatedTokenAddress, toPublicKey } from '../utils/solanaHelper';
import { CgcTokenClaim, IDL } from '../anchor/cgc_token_claim';
import { getDistributorKey, getUserClaimStatusKey } from '../utils/claimHelper';
import useClaimInfo from "./useClaimInfo";

const claimProgramId = toPublicKey(CGC_TOKEN_CLAIM_PROGRAM_ID);

export enum ClaimStatusCode {
  NONE = 0,
  START,
  CLAIMING,
  SUBMITTING,
  SUCCESS,
  FAILED,
}

export enum ClaimErrorCode {
  INVALID_CLAIMANT_HANDLE = 101,
  INVALID_CLAIMANT_HANDLE_MISMATCH = 102,
  INVALID_CLAIMANT_DISTRIBUTOR = 103,
  INVALID_CLAIMANT_DISTRIBUTOR_NONE = 111,
  INVALID_CLAIMANT_DISTRIBUTOR_OWNER = 112,
  INVALID_CLAIMANT_TOKEN_ACCOUNT = 121,
  INVALID_CLAIMANT_TOKEN = 122,
  INVALID_CLAIMANT_PROOF = 131,
  INVALID_CLAIMANT_PROOF_MISMATCH = 132,
}

interface IClaimStatus {
  claim(params: any): void;
  isProcessing: boolean;
  isReady: boolean;
  statusCode: ClaimStatusCode;
  lastError: string | null;
  targetTxId: string;
}

const createClaimStatus = (
  claim: (params: any) => void,
  isProcessing: boolean,
  isReady: boolean,
  statusCode = ClaimStatusCode.NONE,
  lastError: string | null,
  targetTxId: string,
) => ({
  claim,
  isProcessing,
  isReady,
  statusCode,
  lastError,
  targetTxId,
});

function useClaim(): IClaimStatus {
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusCode, setStatusCode] = useState(ClaimStatusCode.NONE);
  const [targetTxId, setTargetTxId] = useState('');
  const [lastError, setLastError] = useState('');
  const walletSolana = useSolanaWallet();
  const { claimInfo } = useClaimInfo();
  const solanaConnection = useMemo(
    () => new Connection(SOLANA_HOST, 'confirmed'),
    [],
  );

  const getAnchorProvider = useCallback(
    async () =>
      new AnchorProvider(
        solanaConnection,
        walletSolana as unknown as AnchorWallet,
        {
          preflightCommitment: `processed`,
          commitment: `confirmed`,
        },
      ),
    [solanaConnection, walletSolana],
  );

  const setError = (errorMessage: string) => {
    setStatusCode(ClaimStatusCode.FAILED);
    setLastError(errorMessage);
    setIsProcessing(false);
  };

  const walletKeyOrPda = async (
    walletKey: PublicKey,
    handle: string,
    seed: PublicKey,
  ): Promise<[PublicKey, Array<Buffer>]> => {
    let key;
    try {
      key = new PublicKey(handle);
      ConsoleHelper(`Seed: ${seed}`);
    } catch (err) {
      ConsoleHelper(JSON.stringify(err));
      throw new Error(
        `Invalid URL (error: ${ClaimErrorCode.INVALID_CLAIMANT_HANDLE})`,
      );
    }

    if (!key.equals(walletKey)) {
      throw new Error(
        `Claimant wallet handle does not match connected wallet (error: 
        ${ClaimErrorCode.INVALID_CLAIMANT_HANDLE_MISMATCH})`,
      );
    }
    return [key, []];
  };

  const submitClaimResult = async (
    address: string,
    txId: string,
    distributor: string,
    index: string | number,
  ) => {
    setTargetTxId(txId);
    const url = URL_SUBMIT_CLAIM(address, txId, distributor, index);
    axios.get(url).then(
      (results) => {
        ConsoleHelper(`processStakeResult: ${JSON.stringify(results)}`);
        if (results.data.success) {
          setStatusCode(ClaimStatusCode.SUCCESS);
          setIsProcessing(false);
        } else {
          const errorMessage = results.data.error_message || 'Unknown error';
          setError(`${errorMessage} (Error code: ${results.data.error_code})`);
        }
      },
      (error) => {
        ConsoleHelper(`processStakeResult: ${error}`);
        setError(`Unknown error`);
      },
    );
  };

  const processClaim = async (params: any) => {
    const { publicKey: walletPublicKey } = walletSolana;
    if (!solanaConnection || !walletPublicKey) {
      throw new Error(`Wallet not connected`);
    }
    const provider = await getAnchorProvider();
    if (!provider) {
      return;
    }
    setIsProcessing(true);
    setStatusCode(ClaimStatusCode.START);

    const anchorProgram = new AnchorProgram<CgcTokenClaim>(
      IDL,
      claimProgramId,
      provider,
    );

    const distributorKey = await getDistributorKey(claimProgramId);
    const tokenMint = toPublicKey(SOLCHICK_TOKEN_ADDRESS_ON_SOL);
    const [userClaimStatusKey, userClaimStatusBump] =
      await getUserClaimStatusKey(claimProgramId, walletPublicKey);
    const tokenFrom = await getAssociatedTokenAddress(
      tokenMint,
      TOKEN_VAULT_WALLET,
    );
    const tokenTo = await getAssociatedTokenAddress(tokenMint, walletPublicKey);

    ConsoleHelper(`distributorKey`, distributorKey.toString());
    ConsoleHelper(`tokenMint`, tokenMint.toString());
    ConsoleHelper(`userClaimStatusKey`, userClaimStatusKey.toString());
    ConsoleHelper(`tokenFrom`, tokenFrom.toString());
    ConsoleHelper(`tokenTo`, tokenTo.toString());
    ConsoleHelper(`claimProgramId`, claimProgramId.toString());

    const userToken = await provider.connection.getAccountInfo(
      tokenTo,
    );
    const ixs: any[] = [];
    if (!userToken) {
      const ix = await Token.createAssociatedTokenAccountInstruction(
        ASSOCIATED_TOKEN_PROGRAM_ID,
        TOKEN_PROGRAM_ID,
        toPublicKey(SOLCHICK_TOKEN_ADDRESS_ON_SOL),
        tokenTo,
        provider.wallet.publicKey,
        provider.wallet.publicKey,
      );
      ixs.push(ix);
      ConsoleHelper(`Create token account`);
    } else {
      ConsoleHelper(`Already existing token account`);
    }

    try {
      await anchorProgram.account.userClaimStatusAccount.fetch(
        userClaimStatusKey,
      );
    } catch (e) {
      ixs.push(
        await anchorProgram.methods
          .initUserClaimStatus(userClaimStatusBump)
          .accounts({
            userAuthority: walletPublicKey,
            userClaimStatus: userClaimStatusKey,
            distributorAccount: distributorKey,
            systemProgram: anchor.web3.SystemProgram.programId,
            rent: SYSVAR_RENT_PUBKEY,
          })
          .instruction()
      );
    }
    const merkleProof =
      params.proof === ''
        ? []
        : params.proof.split(',').map((b: string) => {
            const ret = Buffer.from(bs58.decode(b));
            if (ret.length !== 32)
              throw new Error(
                `Invalid URL (error: ${ClaimErrorCode.INVALID_CLAIMANT_PROOF})`,
              );
            return ret;
          });

    setStatusCode(ClaimStatusCode.CLAIMING);
    try {
      const txId = await anchorProgram.methods
        .claim(
          new anchor.BN(params.index),
          new anchor.BN(params.unlocked_amount.toString()),
          new anchor.BN(params.amount.toString()),
          new anchor.BN(params.start_time),
          new anchor.BN(params.period),
          merkleProof,
        )
        .accounts({
          userAuthority: walletPublicKey,
          distributorAccount: distributorKey,
          tokenMint,
          userClaimStatus: userClaimStatusKey,
          from: tokenFrom,
          to: tokenTo,
          tokenProgram: TOKEN_PROGRAM_ID,
        })
        .preInstructions(ixs)
        .rpc();
      setStatusCode(ClaimStatusCode.SUBMITTING);
      await sleep(15_000);
      claimInfo();
      setStatusCode(ClaimStatusCode.SUCCESS);
      setIsProcessing(false);
    } catch (e) {
      ConsoleHelper(`processClaim -> error: `, e);
      setError(`Claim failed with error: ${JSON.stringify(e)}`);
    }
  };

  const claim = async (params: any) => {
    try {
      await processClaim(params);
    } catch (e) {
      ConsoleHelper('error', e);
      // @ts-ignore
      setError(e.message);
    }
  };

  const isReady = useMemo(() => {
    const { publicKey: walletPublicKey } = walletSolana;
    return !(!solanaConnection || !walletPublicKey);
  }, [walletSolana, solanaConnection]);
  return createClaimStatus(
    claim,
    isProcessing,
    isReady,
    statusCode,
    lastError,
    targetTxId,
  );
}

export default useClaim;
